import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Select,
  Spin,
  Tag,
  Progress,
  Divider,
  Space,
  Typography,
  Alert
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RiseOutlined,
  FallOutlined,
  FileTextOutlined,
  CalendarOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import dayjs from 'dayjs';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import {
  getHRDashboardSummary,
  getDepartmentDistribution,
  getAttendanceTrends,
  getPayrollSummary,
  getAuditLogs
} from '../../api/hrApi';
import './HRAnalyticsDashboard.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title: AntTitle, Text } = Typography;

const HRAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [departmentData, setDepartmentData] = useState([]);
  const [attendanceTrends, setAttendanceTrends] = useState([]);
  const [payrollData, setPayrollData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [selectedYear, setSelectedYear] = useState(dayjs().year().toString());

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (dateRange && dateRange.length === 2) {
      fetchAttendanceTrends();
    }
  }, [dateRange]);

  useEffect(() => {
    fetchPayrollData();
  }, [selectedYear]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [summaryRes, deptRes, auditRes] = await Promise.all([
        getHRDashboardSummary(),
        getDepartmentDistribution(),
        getAuditLogs(50)
      ]);

      if (summaryRes.success) {
        setDashboardData(summaryRes.data);
      }
      if (deptRes.success) {
        setDepartmentData(deptRes.data);
      }
      if (auditRes.success) {
        setAuditLogs(auditRes.data.logs);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceTrends = async () => {
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const res = await getAttendanceTrends(startDate, endDate);
      if (res.success) {
        setAttendanceTrends(res.data.trends);
      }
    } catch (error) {
      console.error('Error fetching attendance trends:', error);
    }
  };

  const fetchPayrollData = async () => {
    try {
      const res = await getPayrollSummary(null, selectedYear);
      if (res.success) {
        setPayrollData(res.data);
      }
    } catch (error) {
      console.error('Error fetching payroll data:', error);
    }
  };

  // Chart configurations
  const departmentChartData = {
    labels: departmentData.map(d => d.department_name),
    datasets: [
      {
        label: 'Active Employees',
        data: departmentData.map(d => d.active_count),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'Inactive Employees',
        data: departmentData.map(d => d.inactive_count),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }
    ]
  };

  const attendanceChartData = {
    labels: attendanceTrends.map(t => dayjs(t.date).format('MMM DD')),
    datasets: [
      {
        label: 'Present',
        data: attendanceTrends.map(t => t.present),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Absent',
        data: attendanceTrends.map(t => t.absent),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Late',
        data: attendanceTrends.map(t => t.late),
        borderColor: 'rgb(255, 205, 86)',
        backgroundColor: 'rgba(255, 205, 86, 0.2)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const attendanceStatusData = dashboardData?.attendance?.currentMonth
    ? {
        labels: ['Present', 'Absent', 'Late', 'Half Day', 'Leave', 'Holiday'],
        datasets: [
          {
            data: [
              dashboardData.attendance.currentMonth.present,
              dashboardData.attendance.currentMonth.absent,
              dashboardData.attendance.currentMonth.late,
              dashboardData.attendance.currentMonth.half_day,
              dashboardData.attendance.currentMonth.leave,
              dashboardData.attendance.currentMonth.holiday
            ],
            backgroundColor: [
              'rgba(75, 192, 192, 0.6)',
              'rgba(255, 99, 132, 0.6)',
              'rgba(255, 205, 86, 0.6)',
              'rgba(153, 102, 255, 0.6)',
              'rgba(54, 162, 235, 0.6)',
              'rgba(201, 203, 207, 0.6)'
            ],
            borderColor: [
              'rgb(75, 192, 192)',
              'rgb(255, 99, 132)',
              'rgb(255, 205, 86)',
              'rgb(153, 102, 255)',
              'rgb(54, 162, 235)',
              'rgb(201, 203, 207)'
            ],
            borderWidth: 1
          }
        ]
      }
    : null;

  const departmentColumns = [
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department_name',
      render: (text, record) => (
        <Space>
          <TeamOutlined style={{ color: '#1890ff' }} />
          <Text strong>{text}</Text>
          <Tag color="blue">{record.department_code}</Tag>
        </Space>
      )
    },
    {
      title: 'Total Employees',
      dataIndex: 'employee_count',
      key: 'employee_count',
      align: 'center',
      render: (count) => <Tag color="geekblue">{count}</Tag>
    },
    {
      title: 'Active',
      dataIndex: 'active_count',
      key: 'active_count',
      align: 'center',
      render: (count) => <Tag color="green">{count}</Tag>
    },
    {
      title: 'Inactive',
      dataIndex: 'inactive_count',
      key: 'inactive_count',
      align: 'center',
      render: (count) => <Tag color="red">{count}</Tag>
    },
    {
      title: 'Avg Salary',
      dataIndex: 'avg_salary',
      key: 'avg_salary',
      align: 'right',
      render: (salary) => `฿${parseFloat(salary || 0).toFixed(2)}`
    }
  ];

  const auditLogColumns = [
    {
      title: 'Table',
      dataIndex: 'table_name',
      key: 'table_name',
      render: (text) => <Tag color="purple">{text}</Tag>
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => {
        const colors = { INSERT: 'green', UPDATE: 'blue', DELETE: 'red' };
        return <Tag color={colors[action] || 'default'}>{action}</Tag>;
      }
    },
    {
      title: 'Record ID',
      dataIndex: 'record_id',
      key: 'record_id'
    },
    {
      title: 'Changed By',
      dataIndex: 'changed_by',
      key: 'changed_by'
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm')
    }
  ];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <Header />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Spin size="large" tip="Loading HR Analytics..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header />
      <div className="hr-analytics-dashboard">
        <div style={{ padding: '24px', background: '#f0f2f5' }}>
          <AntTitle level={2}>
            <TeamOutlined /> HR Analytics Dashboard
          </AntTitle>
          
          {/* Employee Statistics Cards */}
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Employees"
                  value={dashboardData?.employees?.total || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
                <Text type="secondary">
                  Active: {dashboardData?.employees?.active || 0} | 
                  Inactive: {dashboardData?.employees?.inactive || 0}
                </Text>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Current Month Attendance"
                  value={dashboardData?.attendance?.currentMonth?.present || 0}
                  prefix={<CheckCircleOutlined />}
                  suffix={`/ ${dashboardData?.attendance?.currentMonth?.employees_tracked || 0}`}
                  valueStyle={{ color: '#1890ff' }}
                />
                <Text type="secondary">
                  Present days this month
                </Text>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Payroll (YTD)"
                  value={dashboardData?.payroll?.yearToDate?.total_net_salary || 0}
                  prefix={<DollarOutlined />}
                  precision={2}
                  valueStyle={{ color: '#cf1322' }}
                />
                <Text type="secondary">
                  {dashboardData?.payroll?.yearToDate?.total_batches || 0} batches processed
                </Text>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Average Hours/Day"
                  value={dashboardData?.attendance?.currentMonth?.avg_hours_per_day || 0}
                  prefix={<ClockCircleOutlined />}
                  precision={2}
                  suffix="hrs"
                  valueStyle={{ color: '#722ed1' }}
                />
                <Text type="secondary">
                  Overtime: {dashboardData?.attendance?.currentMonth?.total_overtime_hours || 0} hrs
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Department Distribution */}
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={16}>
              <Card title={<><TeamOutlined /> Department Distribution</>}>
                <div style={{ height: 400 }}>
                  <Bar data={departmentChartData} options={chartOptions} />
                </div>
              </Card>
            </Col>
            
            <Col xs={24} lg={8}>
              <Card title={<><CheckCircleOutlined /> Attendance Status</>}>
                {attendanceStatusData && (
                  <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Doughnut data={attendanceStatusData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Attendance Trends */}
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card 
                title={<><CalendarOutlined /> Attendance Trends</>}
                extra={
                  <RangePicker
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates)}
                    format="YYYY-MM-DD"
                  />
                }
              >
                <div style={{ height: 350 }}>
                  <Line data={attendanceChartData} options={chartOptions} />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Department Details Table */}
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card title={<><TeamOutlined /> Department Details</>}>
                <Table
                  columns={departmentColumns}
                  dataSource={departmentData}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 800 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Payroll Summary */}
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <Card 
                title={<><DollarOutlined /> Payroll Summary (Year to Date)</>}
                extra={
                  <Select
                    value={selectedYear}
                    onChange={setSelectedYear}
                    style={{ width: 120 }}
                  >
                    <Option value="2026">2026</Option>
                    <Option value="2025">2025</Option>
                    <Option value="2024">2024</Option>
                  </Select>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Total Gross Salary"
                        value={dashboardData?.payroll?.yearToDate?.total_gross_salary || 0}
                        precision={2}
                        prefix="฿"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Total Deductions"
                        value={dashboardData?.payroll?.yearToDate?.total_deductions || 0}
                        precision={2}
                        prefix="฿"
                        valueStyle={{ color: '#cf1322' }}
                      />
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Total Bonus"
                        value={dashboardData?.payroll?.yearToDate?.total_bonus || 0}
                        precision={2}
                        prefix="฿"
                        valueStyle={{ color: '#3f8600' }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Total Overtime"
                        value={dashboardData?.payroll?.yearToDate?.total_overtime || 0}
                        precision={2}
                        prefix="฿"
                        valueStyle={{ color: '#722ed1' }}
                      />
                    </Col>
                  </Row>
                  <Divider />
                  <Row>
                    <Col span={24}>
                      <Statistic
                        title="Net Payroll Amount"
                        value={dashboardData?.payroll?.yearToDate?.total_net_salary || 0}
                        precision={2}
                        prefix="฿"
                        valueStyle={{ color: '#1890ff', fontSize: 28 }}
                      />
                      <Text type="secondary">
                        Completed: {dashboardData?.payroll?.yearToDate?.completed_batches || 0} | 
                        Draft: {dashboardData?.payroll?.yearToDate?.draft_batches || 0} | 
                        Processing: {dashboardData?.payroll?.yearToDate?.processing_batches || 0}
                      </Text>
                    </Col>
                  </Row>
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title={<><ClockCircleOutlined /> Current Month Summary</>}>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Work Days"
                        value={dashboardData?.payroll?.currentMonth?.total_work_days || 0}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Absent Days"
                        value={dashboardData?.payroll?.currentMonth?.total_absent_days || 0}
                        prefix={<CloseCircleOutlined />}
                        valueStyle={{ color: '#cf1322' }}
                      />
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Gross Salary"
                        value={dashboardData?.payroll?.currentMonth?.total_gross || 0}
                        precision={2}
                        prefix="฿"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Net Salary"
                        value={dashboardData?.payroll?.currentMonth?.total_net_salary || 0}
                        precision={2}
                        prefix="฿"
                        valueStyle={{ color: '#3f8600' }}
                      />
                    </Col>
                  </Row>
                  <Progress
                    percent={
                      dashboardData?.payroll?.currentMonth?.total_gross
                        ? Math.round(
                            (dashboardData.payroll.currentMonth.total_net_salary /
                              dashboardData.payroll.currentMonth.total_gross) *
                              100
                          )
                        : 0
                    }
                    status="active"
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068'
                    }}
                  />
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Audit Logs */}
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card title={<><FileTextOutlined /> Recent Audit Logs</>}>
                <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                  <Alert
                    message="Audit Trail"
                    description={`Last 30 days: ${dashboardData?.auditLog?.last30Days?.total_changes || 0} changes | 
                      Insertions: ${dashboardData?.auditLog?.last30Days?.insertions || 0} | 
                      Updates: ${dashboardData?.auditLog?.last30Days?.updates || 0} | 
                      Deletions: ${dashboardData?.auditLog?.last30Days?.deletions || 0}`}
                    type="info"
                    showIcon
                    icon={<SafetyOutlined />}
                  />
                </Space>
                <Table
                  columns={auditLogColumns}
                  dataSource={auditLogs}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </Layout>
  );
};

export default HRAnalyticsDashboard;
